import type {
  AdminUserDetail,
  AdminUserPause,
  AdminUserPaybackGap,
  AdminUserPaybackMeditation,
  AdminUserPaybackPause,
  AdminUserPaybackRefund,
  AdminUserPaybackRefundItem,
  AdminUserPaybackResult,
  AdminUserPaybackReview,
  AdminUserPaybackThought,
  AdminUserPaybackWeek,
  AdminUserPayment,
  AdminUserRefundResult,
  AdminUserSelfCheckPoint,
  AdminUserSubscription,
  AdminUserSubscriptionResult,
  AdminUserWebFunnelChannel,
} from "api/adminApi";
import { selfCheckData } from "data/selfCheckData";

// 회원관리 화면의 표시 변환 모음. JSX 없이 순수 함수만 둔다 (테스트 대상).
// AdminPage.tsx 안에도 같은 이름의 로컬 헬퍼가 있지만 가져다 쓰지 않는다 —
// 회원관리는 체험 미리보기가 다루지 않던 상태(휴면·종결 권고 등)까지 표시해야 해서 매핑이 다르다.

const PROGRESS_STATUS_LABEL: Record<string, string> = {
  preparing: "준비 중",
  in_progress: "이용 중",
  terminated: "종료",
  recommended_stop: "종결 권고",
  leave: "탈퇴",
  auto_stop: "자동 종료",
};

export const progressStatusLabel = (status?: string | null) =>
  (status && PROGRESS_STATUS_LABEL[status]) || status || "알 수 없음";

const SUBSCRIPTION_STATUS_LABEL: Record<string, string> = {
  freetrial: "체험 중",
  freetrial_expired: "체험 만료",
  subscribing: "이용 중",
  subscribing_expired: "이용 종료",
};

export const subscriptionStatusLabel = (status?: string | null) =>
  (status && SUBSCRIPTION_STATUS_LABEL[status]) || status || "없음";

const PLAN_LABEL: Record<string, string> = {
  starter: "스타터",
  challenger: "챌린저",
  master: "마스터",
};

export const planLabel = (plan?: string | null) =>
  (plan && PLAN_LABEL[plan]) || plan || "없음";

const OAUTH_ISSUER_LABEL: Record<string, string> = {
  kakao: "카카오",
  google: "구글",
  apple: "애플",
  naver: "네이버",
};

export const oauthIssuerLabel = (issuer?: string | null) =>
  (issuer && OAUTH_ISSUER_LABEL[issuer]) || issuer || "알 수 없음";

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  ready: "대기",
  success: "승인 대기",
  confirmed: "결제 완료",
  canceled: "취소",
  failed: "실패",
};

export const paymentStatusLabel = (status?: string | null) =>
  (status && PAYMENT_STATUS_LABEL[status]) || status || "알 수 없음";

const VENDOR_TYPE_LABEL: Record<string, string> = {
  toss_payments: "토스",
  app_store: "앱스토어",
  play_store: "플레이스토어",
  steppay: "스텝페이",
};

export const vendorTypeLabel = (vendor?: string | null) =>
  (vendor && VENDOR_TYPE_LABEL[vendor]) || vendor || "알 수 없음";

// 정지 상태 한 줄. 무기한이면 종료 예정일이 없다는 것을 분명히 말해 준다.
export const pauseStateLabel = (pause: AdminUserPause) => {
  if (!pause.isPaused) return "정지 아님";
  if (!pause.resumedAt) return "무기한 정지";
  return `${formatDate(pause.resumedAt)} 재개 예정`;
};

// "2026-08-27T14:03:22" 또는 "2026-08-27" 을 "2026. 8. 27." 로. 값이 없으면 빈 문자열.
export const formatDate = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const formatDateTime = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatAmount = (amount?: number | null) =>
  amount == null ? "" : `${amount.toLocaleString("ko-KR")}원`;

/**
 * 결제 이력의 "결제 금액" 칸.
 *
 * payAmount 는 플랜 변경·금액만 환불로 깎인 잔액이라 누적 환불 금액과 기준이 어긋난다 — 다운그레이드 뒤
 * 자발 환불이면 환불 금액이 결제 금액보다 커 보인다. 서버가 토스 기준 원 결제 금액을 주면 그것을 쓴다.
 */
export const paidAmountLabel = (payment: AdminUserPayment): string =>
  formatAmount(payment.originalAmount ?? payment.payAmount);

/**
 * 결제 이력의 "환불 금액" 칸.
 *
 * 결제 금액만으로는 실제로 얼마가 나갔는지 알 수 없다 — 수수료 제외 환불(10% 공제)은 부분 취소라
 * 결제 금액 그대로 두고 90%만 돌려준다. 금액만 환불은 반대로 취소일 없이 결제 금액을 깎는다.
 * 그래서 취소 여부가 아니라 누적 취소 금액이 있는지로 판단한다.
 *
 * 취소된 건인데 값이 없으면 canceled_amount 컬럼 신설 전 건이다 — 빈칸으로 두면 "0원 환불"로 읽힌다.
 */
export const refundedAmountLabel = (payment: AdminUserPayment): string => {
  if (payment.canceledAmount != null)
    return formatAmount(payment.canceledAmount);
  return payment.canceledAt ? "기록 없음" : "";
};

// yyyy-MM-dd — date 인풋 값과 서버 파라미터가 같은 형식이다.
export const toDateInputValue = (date: Date) => {
  const pad = (n: number) => `${n}`.padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const todayInputValue = () => toDateInputValue(new Date());

// 프로그램 시작 팝업의 기본 이용 종료일 — 오늘부터 100일.
// 서버가 untilDate 를 비우면 쓰는 기본값(AdminUserBO.DEFAULT_START_DAYS)과 같아야 화면과 결과가 어긋나지 않는다.
export const defaultStartUntilValue = () => {
  const date = new Date();
  date.setDate(date.getDate() + 100);
  return toDateInputValue(date);
};

// yyyy-MM-dd 를 로컬 자정 Date 로 읽는다. new Date("2026-10-05") 는 UTC 자정이라
// 시간대에 따라 하루가 밀린다 — 날짜 계산은 항상 이쪽을 거친다.
const parseDateInputValue = (value?: string | null): Date | null => {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? "").trim());
  if (!matched) return null;
  const date = new Date(
    Number(matched[1]),
    Number(matched[2]) - 1,
    Number(matched[3])
  );
  return Number.isNaN(date.getTime()) ? null : date;
};

// yyyy-MM-dd 에 일수를 더한다 (음수면 뺀다). 날짜 형식이 아니면 null.
export const shiftDateInputValue = (
  value: string | null | undefined,
  days: number
): string | null => {
  const date = parseDateInputValue(value);
  if (!date || !Number.isFinite(days)) return null;
  date.setDate(date.getDate() + days);
  // 일수가 Date 범위를 넘으면 NaN 이 된다 — "NaN-NaN-NaN" 을 내보내지 않는다
  if (Number.isNaN(date.getTime())) return null;
  return toDateInputValue(date);
};

// 두 yyyy-MM-dd 사이의 일수 (to - from). 하나라도 날짜 형식이 아니면 null.
export const daysBetweenInputValues = (
  from: string | null | undefined,
  to: string | null | undefined
): number | null => {
  const start = parseDateInputValue(from);
  const end = parseDateInputValue(to);
  if (!start || !end) return null;
  // 서머타임이 없는 KST 기준이지만, 혹시 걸려도 하루가 어긋나지 않게 반올림한다
  return Math.round((end.getTime() - start.getTime()) / 86400000);
};

// yyyy-MM-dd 를 "10월 6일" 로 — 한 문장 안에 날짜가 여럿 들어가는 요약에 쓴다. 날짜가 아니면 빈 문자열.
export const monthDayLabel = (value?: string | null) => {
  const date = parseDateInputValue(value);
  return date ? `${date.getMonth() + 1}월 ${date.getDate()}일` : "";
};

// 어드민이 미래 날짜로 잡아 둔 정지 한 줄 — "10월 6일부터 무기한" / "10월 6일 ~ 10월 19일". 예약이 없으면 null.
export const pauseScheduleLabel = (pause: AdminUserPause): string | null => {
  if (!pause.scheduledStartDate) return null;
  const start = monthDayLabel(pause.scheduledStartDate);
  return pause.scheduledEndDate
    ? `${start} ~ ${monthDayLabel(pause.scheduledEndDate)}`
    : `${start}부터 무기한`;
};

// 정지 예약을 잡은 사람과 날 — "bglee · 9월 28일". 처리 이력 표처럼 회사 도메인은 뗀다.
export const pauseScheduledByLabel = (pause: AdminUserPause): string =>
  [
    pause.scheduledBy?.replace("@orwellhealth.org", ""),
    monthDayLabel(pause.scheduledAt?.slice(0, 10)),
  ]
    .filter(Boolean)
    .join(" · ");

// 예약은 시작일 07:20 에 실행되고 지워진다. 그 뒤에도 남아 있으면 실행이 돌지 못한 것이다 — 잡은 다시 돌지 않으므로
// 그날 아침이 지나면(9시) 바로 알린다. 시작일 당일 이른 아침은 아직 실행 전일 수 있다.
const PAUSE_SCHEDULE_CHECK_HOUR = 9;

export const isPauseScheduleMissed = (
  pause: AdminUserPause,
  now: Date = new Date()
): boolean => {
  const fromToday = daysBetweenInputValues(
    toDateInputValue(now),
    pause.scheduledStartDate
  );
  if (fromToday === null) return false;
  return (
    fromToday < 0 ||
    (fromToday === 0 && now.getHours() >= PAUSE_SCHEDULE_CHECK_HOUR)
  );
};

export type PausePlan = {
  // 미래 날짜부터면 예약, 오늘이면 바로 정지
  isFuture: boolean;
  // 언제 멈추고 언제 풀리는지, 기한이 며칠 늘어나는지 한 줄 요약. 보낼 수 없으면 null
  summary: string | null;
  // 이대로는 보낼 수 없는 이유
  error: string | null;
};

/**
 * 일시정지 팝업 미리보기 (서버 AdminUserBO.pause).
 *
 * 막는 규칙은 서버와 같다 — 시작일은 오늘 이후, 미래 날짜면 이용 기한 날짜보다 앞(기한 당일은 실행 시각에 이미
 * 만료됐을 수 있다), 마지막 날은 시작일 이후. 기한부는 정지 일수(양끝 포함)만큼 이용 기한이 정지 시작 때 늘어난다.
 * endDate 가 null 이면 무기한이다.
 */
export const pausePlanOf = (
  startDate: string,
  endDate: string | null,
  expirationDate?: string | null
): PausePlan => {
  const fromToday = daysBetweenInputValues(todayInputValue(), startDate);
  if (fromToday === null)
    return {
      isFuture: false,
      summary: null,
      error: "정지 시작일을 골라 주세요",
    };
  if (fromToday < 0)
    return {
      isFuture: false,
      summary: null,
      error: "오늘 이전으로는 잡을 수 없어요",
    };

  const isFuture = fromToday > 0;
  const untilExpiration = daysBetweenInputValues(startDate, expirationDate);
  if (isFuture && untilExpiration !== null && untilExpiration <= 0)
    return {
      isFuture,
      summary: null,
      error: `정지 시작일은 이용 기한(${monthDayLabel(expirationDate)})보다 앞이어야 해요`,
    };

  const start = monthDayLabel(startDate);
  // 무기한은 기한을 지금 건드리지 않고, 어드민이 재개할 때 정지한 일수만큼 더한다 (서버 resume)
  if (endDate === null)
    return {
      isFuture,
      error: null,
      summary: `${
        isFuture ? `${start} 아침에 정지가 시작돼요.` : "지금 바로 정지돼요."
      } 재개는 어드민이 직접 하고, 그때 정지한 일수만큼 이용 기한이 늘어나요.`,
    };

  const span = daysBetweenInputValues(startDate, endDate);
  if (span === null)
    return {
      isFuture,
      summary: null,
      error: "정지 마지막 날을 골라 주세요",
    };
  if (span < 0)
    return {
      isFuture,
      summary: null,
      error: "정지 마지막 날은 시작일보다 앞일 수 없어요",
    };
  const days = span + 1;
  const resume = monthDayLabel(shiftDateInputValue(endDate, 1));
  return {
    isFuture,
    error: null,
    summary: isFuture
      ? `${start} 아침에 정지가 시작되고 ${resume} 아침에 자동으로 재개돼요. 시작할 때 이용 기한이 ${days}일 늘어나요.`
      : `지금 바로 정지되고 ${resume} 아침에 자동으로 재개돼요. 정지한 ${days}일만큼 이용 기한이 바로 늘어나요.`,
  };
};

export type ExpirationAdjustDirection = "extend" | "shorten";

// 연장 ↔ 단축. 기간의 반대말은 축소(크기)가 아니라 단축이다.
export const EXPIRATION_ADJUST_LABEL: Record<
  ExpirationAdjustDirection,
  string
> = {
  extend: "연장",
  shorten: "단축",
};

export type ExpirationAdjust = {
  // 조정 뒤 이용 기한 (yyyy-MM-dd). 입력으로 계산할 수 없으면 null
  nextDate: string | null;
  // 현재 기한과 비교한 방향. 변화가 없거나 현재 기한을 모르면 null
  direction: ExpirationAdjustDirection | null;
  // 며칠 움직이는지 (항상 양수)
  days: number;
  // 이대로는 보낼 수 없는 이유
  error: string | null;
};

/**
 * 기한 조정 미리보기 — 바뀔 이용 기한과 방향(연장/단축)을 계산한다.
 *
 * 방향은 어떤 방식을 골랐는지가 아니라 **현재 기한과 비교한 결과**로 정한다. "오늘부터 30일" 처럼
 * 늘리려고 고른 방식이 실제로는 기한을 당기는 경우가 있어서, 화면 문구는 계산 결과를 따라간다.
 *
 * 결과가 오늘이거나 그 이전이면 방향과 무관하게 막는다(서버 ADMIN_USER_EXPIRATION_NOT_FUTURE 와 같은 선).
 * 줄이는 쪽만 아니라 "3주 전 만료 + 14일" 같은 연장도 여기 걸린다. 오늘도 막는 이유는 만료 크론이
 * 지난 기한만 훑기 때문이다 — 오늘로 잡으면 남은 하루를 주고 다음 날 아침에야 종료 푸시가 나간다.
 * 규칙이 날짜로만 떨어져서 화면이 서버와 같은 판정을 할 수 있다 (시각은 볼 필요가 없다).
 */
export const expirationAdjustOf = (
  currentDate: string | null | undefined,
  nextDate: string | null | undefined
): ExpirationAdjust => {
  const next = parseDateInputValue(nextDate) ? nextDate!.trim() : null;
  if (!next) return { nextDate: null, direction: null, days: 0, error: null };

  const delta = daysBetweenInputValues(currentDate, next);
  // 현재 기한을 모르면 방향도 알 수 없다 (화면은 기한이 있는 회원만 열어 준다)
  if (delta === null)
    return { nextDate: next, direction: null, days: 0, error: null };
  if (delta === 0)
    return {
      nextDate: next,
      direction: null,
      days: 0,
      error: "지금 이용 기한과 같아요",
    };

  const direction: ExpirationAdjustDirection = delta > 0 ? "extend" : "shorten";
  const fromToday = daysBetweenInputValues(todayInputValue(), next);
  const notFuture = fromToday !== null && fromToday <= 0;
  return {
    nextDate: next,
    direction,
    days: Math.abs(delta),
    error: !notFuture
      ? null
      : direction === "shorten"
        ? "이용 기한은 오늘보다 뒤 날짜여야 해요. 오늘로 끝내려면 프로그램 종료로 처리해 주세요"
        : "이용 기한은 오늘보다 뒤 날짜여야 해요. 오늘부터 계산하기를 켜거나 더 뒤 날짜로 잡아 주세요",
  };
};

/**
 * 환불이 붙은 종료의 낙관적 결과.
 *
 * 서버는 토스 취소만 하고 회원 상태는 웹훅이 몇 초 뒤에 바꾸므로 환불 응답에는 구독 상태가 없다.
 * 웹훅 종료 경로가 할 일을 화면이 먼저 그린다 — 종료 + 이용 종료, 정지 해제. 이용 기한은 웹훅과 같은
 * 규칙으로 이용 중일 때만 오늘로 당긴다 (정지 중이면 이미 만료 상태라 웹훅도 그대로 둔다).
 */
export const optimisticTerminateResult = (
  detail: AdminUserDetail
): AdminUserSubscriptionResult => ({
  progressStatus: "terminated",
  subscriptionStatus: "subscribing_expired",
  subscriptionExpirationDate:
    detail.subscription.status === "subscribing"
      ? todayInputValue()
      : detail.subscription.subscriptionExpirationDate,
  pausedAt: null,
  resumedAt: null,
  bonusDays: detail.subscription.bonusDays,
});

/**
 * 종료 환불 응답을 결제 이력에 먼저 반영한다.
 *
 * 토스 취소는 이미 끝났다(서버는 토스가 성공을 돌려준 뒤에야 응답한다) — 표만 웹훅을 기다리고 있을 뿐이다.
 * 취소된 건은 취소 처리 + 누적 환불 금액을 더하고, 환불 가능 표시를 내려 웹훅이 오기 전 같은 건에
 * 환불이 한 번 더 나가는 창을 닫는다. 결제 금액은 서버 종료 경로와 같이 그대로 둔다.
 */
export const refundedPayments = (
  payments: AdminUserPayment[],
  refund: AdminUserRefundResult
): AdminUserPayment[] => {
  const now = new Date().toISOString();
  return payments.map((payment) => {
    const row = refund.canceled.find((r) => r.orderId === payment.orderId);
    if (!row) return payment;
    return {
      ...payment,
      status: "canceled",
      canceledAt: payment.canceledAt ?? now,
      canceledAmount: (payment.canceledAmount ?? 0) + row.cancelAmount,
      originalAmount: payment.originalAmount ?? payment.payAmount,
      isRefundable: false,
    };
  });
};

/**
 * 재개 시 자동 복원될 만료일 = 서버가 계산한 autoResumeExpirationDate + 추가 일수.
 * 식을 화면에서 복제하지 않는다 — 정지 시작 때 만료일을 연장한 정지(미사용 일수 회수)인지
 * 옛 방식 정지(경과 일수 가산)인지는 서버만 알기 때문. 값이 없으면 "" (화면은 직접 지정을 안내한다).
 */
export const restoredExpirationDate = (
  pause: AdminUserPause,
  extraDays: number
): string => {
  const base = pause.autoResumeExpirationDate;
  if (!base) return "";
  const date = new Date(base);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + extraDays);
  return toDateInputValue(date);
};

// 이용 상태 배지 톤 — 색은 장식이 아니라 상태를 읽는 축이다.
export const subscriptionTone = (
  pause: AdminUserPause,
  subscription: AdminUserSubscription
): "ok" | "warn" | "idle" | "info" => {
  if (pause.isPaused) return "warn";
  if (
    subscription.status === "subscribing" ||
    subscription.status === "freetrial"
  )
    return "ok";
  if (!subscription.hasSubscription) return "info";
  return "idle";
};

// 어드민이 이 회원에게 할 수 있는 처리. 서버 엔드포인트와 1:1 이다.
// refundTerminate 만 액션 바에 버튼이 없다 — 프로그램 종료 팝업의 "환불도 같이" 체크가 이 처리다.
export type AdminUserAction =
  | "start"
  | "reset"
  | "refundTerminate"
  | "refundDowngrade"
  | "refundAmount"
  | "payback"
  | "extend"
  | "pause"
  | "pauseCancel"
  | "resume"
  | "terminate"
  | "nickname"
  | "coach";

/**
 * 이 회원에게 이 처리를 할 수 없는 이유. 할 수 있으면 null.
 *
 * 판정 기준을 서버(AdminUserBO)가 실제로 던지는 전제조건에 맞춘다. 화면이 서버보다 넓게 막으면
 * 정상 처리를 눌러 보지도 못하고, 좁게 막으면 눌러야만 안 된다는 걸 알게 된다.
 * 만료일은 서버가 시각까지 보는데 화면은 날짜만 받으므로, "오늘 만료" 처럼 애매한 경우는 막지 않고
 * 서버가 판정하게 둔다.
 */
/**
 * 어드민 환불의 기준 결제. 서버(AdminUserBO.refundPlanOf)와 같이 **가장 최근 확정 건**을 잡는다 —
 * 재구독 회원은 확정 결제가 여러 건이라 옛 건을 잡으면 화면과 실제가 어긋난다.
 * 환불 가능 판정은 서버가 내려준 isRefundable 을 그대로 쓴다 (paymentKey 는 화면에 안 내려온다).
 */
export const refundBasePayment = (detail: AdminUserDetail) => {
  const refundable = detail.payments.filter((payment) => payment.isRefundable);
  if (refundable.length === 0) return null;
  return refundable.reduce((latest, payment) =>
    (payment.confirmedAt ?? "") > (latest.confirmedAt ?? "") ? payment : latest
  );
};

/**
 * 상태를 바꾸는 종료(진행 종료 + 이용 기한 당김)가 가능한 회원인지.
 *
 * false 면 이 회원에게 남은 건 환불뿐이다 — 이미 만료·종료된 회원도 환불 요청은 들어오고,
 * 서버 refundPlanOf(TERMINATE) 는 결제와 구독 행만 보지 진행 상태를 보지 않는다.
 */
export const canTerminateState = (detail: AdminUserDetail): boolean =>
  (detail.subscription.status === "subscribing" &&
    detail.user.progressStatus === "in_progress") ||
  detail.pause.isPaused;

/**
 * 협의 금액이 환불 가능 잔액을 넘는지. 넘으면 안내 문구, 아니면 null.
 *
 * 잔액은 원결제 금액이 아니라 **현재 payAmount** 다 — 부분 취소(다운그레이드·금액만 환불)가 있었으면
 * 웹훅이 그만큼 줄여 둔다. 395,000 결제 후 100,000 을 다운그레이드로 돌려줬으면 잔액은 295,000 이고
 * 전액 환불도 295,000 이다.
 *
 * 입력값을 잔액으로 조용히 잘라 버리는 방법도 있지만, 돈이 걸린 칸에서 타이핑한 숫자가 말없이
 * 바뀌면 운영자가 얼마를 보낸 건지 확신할 수 없다. 그대로 두고 왜 못 보내는지 말한다.
 */
export const refundAmountError = (
  amount: string,
  balance?: number | null
): string | null => {
  const value = Number(amount);
  if (!amount.trim() || value <= 0) return null;
  return value > (balance ?? 0)
    ? `환불 가능 잔액 ${formatAmount(balance)}을 넘을 수 없어요`
    : null;
};

export const actionBlockReason = (
  detail: AdminUserDetail,
  action: AdminUserAction
): string | null => {
  const { user, subscription, pause, coach } = detail;

  // 페이백은 탈퇴한 회원도 연다 — 탈퇴한 뒤에도 페이백 문의가 오고, 결제·활동 기록은 탈퇴해도 남는다 (서버도 막지 않는다).
  // 실행할 수 있는지(앱 스토어 결제·이미 페이백함 등)는 창을 열면 서버가 알려 준다.
  // 서버 AdminPaybackReviewBO 의 대상 규칙과 같다: 체험·어드민 시작처럼 결제가 없는 회원은 대상이 아니다.
  if (action === "payback")
    return hasPaidPayment(detail) ? null : "결제 이력이 없는 회원이에요";

  if (user.progressStatus === "leave") return "탈퇴한 계정이에요";

  // 구독 행과 만료일이 있어야 기간을 다룰 수 있다. 준비 중(결제 전) 회원이 여기 걸린다.
  const hasSubscription =
    subscription.hasSubscription && !!subscription.subscriptionExpirationDate;
  // 서버 pause 의 전제조건과 같은 식이다.
  const isSubscribing =
    subscription.status === "subscribing" &&
    user.progressStatus === "in_progress";

  switch (action) {
    // 서버 start 의 전제조건과 같다 — 준비 중이면서 구독 행이 아직 없어야 한다.
    // 구독 행 PK 가 userId 라, 이미 있는 회원을 시작시키면 기존 이력을 덮어쓴다.
    case "start":
      if (user.progressStatus !== "preparing")
        return "준비 중인 회원만 시작할 수 있어요";
      if (subscription.hasSubscription)
        return "이미 구독 정보가 있어요. 기한 조정으로 처리해 주세요";
      if (!user.nickname) return "닉네임을 아직 정하지 않은 회원이에요";
      return null;

    // 서버 reset 의 전제조건과 같다. 시작한 적 없는 회원은 지울 것도 없고,
    // 코치·채팅방 없이 활동지만 열리면 반쪽 상태가 된다.
    case "reset":
      return user.progressStatus === "preparing"
        ? "아직 시작하지 않은 회원이에요"
        : null;

    case "extend":
      return hasSubscription ? null : "이용 정보가 없는 회원이에요";

    case "pause":
      if (pause.isPaused) return "이미 일시정지 중이에요";
      if (!isSubscribing) return "이용 중인 회원만 정지할 수 있어요";
      if (isExpired(subscription.subscriptionExpirationDate))
        return "이용 기한이 이미 지났어요";
      return null;

    case "resume":
      return pause.isPaused ? null : "일시정지 중인 회원이 아니에요";

    case "pauseCancel":
      return pause.scheduledStartDate ? null : "잡아 둔 정지 예약이 없어요";

    // 일시정지는 내부적으로 progressStatus 가 종료로 저장될 뿐 운영자가 보는 상태는 "정지 중" 이라,
    // 무기한 정지 회원의 프로그램 종료는 열어 둔다.
    case "terminate":
      if (!hasSubscription) return "이용 정보가 없는 회원이에요";
      if (canTerminateState(detail)) return null;
      // 상태는 더 바꿀 게 없어도 환불은 남아 있을 수 있다. 그때는 팝업이 환불 전용으로 열린다 —
      // 만료된 회원의 환불 요청을 화면이 막으면 CS 가 토스 콘솔로 돌아가야 한다.
      return refundBasePayment(detail)
        ? null
        : "이용 중이거나 정지 중인 회원만 종료할 수 있어요";

    // 환불 3종은 서버 refundPlanOf 의 전제조건과 같게 막는다.
    case "refundTerminate":
    case "refundDowngrade":
    case "refundAmount": {
      const base = refundBasePayment(detail);
      if (!base) return "환불할 수 있는 토스 결제 건이 없어요";
      if (action === "refundTerminate" && !subscription.hasSubscription) {
        // 웹훅 종료 경로가 구독 행을 반드시 찾는다 — 없으면 결제만 취소되고 웹훅이 죽는다
        return "이용 정보가 없는 회원이에요";
      }
      if (action === "refundDowngrade") {
        if (user.isCarePass)
          return "케어패스 회원은 차액을 자동 계산할 수 없어요";
        // 스타터가 최하위 플랜이다. hints 도 비어 있지만(결제액보다 싼 플랜이 없다) 그건
        // 결제 금액이 모자란 경우와 같은 결과라, 왜 막혔는지 문구로 구분해 준다.
        if (user.plan === "starter") return "이미 가장 낮은 플랜이에요";
        if (base.planChangeHints.length === 0)
          return "내릴 수 있는 하위 플랜이 없어요";
      }
      return null;
    }

    case "coach":
      return coach ? null : "배정된 코치가 없어요";

    // 닉네임은 이용 상태와 무관하게 언제든 바꿀 수 있다.
    case "nickname":
    default:
      return null;
  }
};

// 만료일이 오늘보다 확실히 앞인지. 오늘이면 시각을 알 수 없으므로 지났다고 보지 않는다.
const isExpired = (expirationDate?: string | null) => {
  if (!expirationDate) return true;
  const expiration = new Date(expirationDate);
  if (Number.isNaN(expiration.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiration.setHours(0, 0, 0, 0);
  return expiration.getTime() < today.getTime();
};

// 웹퍼널 utm 파라미터를 한 줄로 합친다. 어떤 조합이 비어 있어도 오해가 없도록 키를 붙여서 낸다
// (값만 나열하면 medium 하나만 있을 때 source 로 읽힌다). 값이 하나도 없으면 null — 줄 자체를 뺀다.
export const utmSummary = (
  webFunnel: AdminUserWebFunnelChannel
): string | null => {
  const pairs: [string, string | null | undefined][] = [
    ["source", webFunnel.utmSource],
    ["medium", webFunnel.utmMedium],
    ["campaign", webFunnel.utmCampaign],
    ["content", webFunnel.utmContent],
    ["term", webFunnel.utmTerm],
  ];
  const parts = pairs
    .filter(([, value]) => !!value?.trim())
    .map(([key, value]) => `${key}=${value!.trim()}`);
  return parts.length ? parts.join(" · ") : null;
};

// ------------------------------------------------------------------ 페이백 검토

// 결제 확정된 적 있는 유료 결제가 있는지 (서버 AdminPaybackReviewBO 와 같은 규칙).
// 부분 취소로 깎인 잔액이 아니라 원 결제 금액으로 보고 0원만 뺀다 — 인앱(스토어) 결제는 금액을 남기지 않아 비어 있다.
export const hasPaidPayment = (detail: AdminUserDetail): boolean =>
  detail.payments.some((payment) => {
    const amount = payment.originalAmount ?? payment.payAmount;
    return !!payment.confirmedAt && (amount == null || amount > 0);
  });

export type SelfCheckKind = "depression" | "anxiety" | "stress";

// 검사 점수의 심각도 — 앱의 검사 결과 화면(selfCheckData)과 같은 구간이다. PHQ-9 20점은 "극심함".
export const selfCheckSeverity = (kind: SelfCheckKind, score: number) =>
  selfCheckData[kind].result.find(
    ({ range: [min, max] }) => score >= min && score <= max
  )?.label ?? "";

// 검사 요약 — 첫 검사와 마지막 검사, 횟수. "20/극심함 (W2) → 5/가벼움 (W12) · 3회". 기록이 없으면 "검사 기록 없음".
// 사이의 점수는 그래프(selfCheckChartPoints)로 본다.
export const selfCheckSummaryLabel = (
  kind: SelfCheckKind,
  points: AdminUserSelfCheckPoint[]
) => {
  if (points.length === 0) return "검사 기록 없음";
  const label = (point: AdminUserSelfCheckPoint) =>
    `${point.score}/${selfCheckSeverity(kind, point.score)}${
      point.week ? ` (W${point.week})` : ""
    }`;
  const first = points[0];
  const last = points[points.length - 1];
  return points.length === 1
    ? `${label(first)} · 1회`
    : `${label(first)} → ${label(last)} · ${points.length}회`;
};

// 검사 만점 — 앱의 검사 결과 구간 중 가장 높은 점수 (PHQ-9 27, GAD-7 21)
export const selfCheckMaxScore = (kind: SelfCheckKind) =>
  Math.max(...selfCheckData[kind].result.map(({ range }) => range[1]));

// 그래프 점선 — "중간"이 시작하는 점수 (우울·불안 10점, 스트레스 16점)
export const selfCheckCutoff = (kind: SelfCheckKind) =>
  selfCheckData[kind].abnormalScore;

// 심각도 단계 — 0 정상, 1 가벼움, 2 중간, 3 이상은 심함·극심함. 그래프의 점 색에 쓴다.
export const selfCheckSeverityLevel = (kind: SelfCheckKind, score: number) =>
  Math.max(
    0,
    selfCheckData[kind].result.findIndex(
      ({ range: [min, max] }) => score >= min && score <= max
    )
  );

// 그래프 점의 툴팁 — 점수와 심각도를 앞에. "20점 · 극심함 · 6월 20일 (W2)", 주차를 모르면 날짜만
export const selfCheckPointLabel = (
  kind: SelfCheckKind,
  point: AdminUserSelfCheckPoint
) =>
  `${point.score}점 · ${selfCheckSeverity(kind, point.score)} · ${monthDayLabel(
    point.date
  )}${point.week ? ` (W${point.week})` : ""}`;

export type SelfCheckChartPoint = {
  x: number;
  y: number;
  point: AdminUserSelfCheckPoint;
};

// 약식 그래프의 점 위치 — 가로는 검사 순서대로 고르게 (날짜 간격은 무시), 세로는 0점(아래) ~ 만점(위).
// 점이 하나면 가운데에 둔다.
export const selfCheckChartPoints = (
  kind: SelfCheckKind,
  points: AdminUserSelfCheckPoint[],
  box: { left: number; right: number; top: number; bottom: number }
): SelfCheckChartPoint[] => {
  const max = selfCheckMaxScore(kind);
  const width = box.right - box.left;
  return points.map((point, index) => ({
    x:
      points.length === 1
        ? box.left + width / 2
        : box.left + (width * index) / (points.length - 1),
    y:
      box.bottom -
      ((box.bottom - box.top) * Math.min(Math.max(point.score, 0), max)) / max,
    point,
  }));
};

// 페이백 조건 한 줄 — 앱 FAQ 의 페이백 규정. met: true 충족 · false 미충족 · null 아직 가릴 수 없음.
// 판단은 CS 가 한다 — 화면은 규정에 비춰 본 사실만 보여준다.
export type PaybackCondition = {
  met: boolean | null;
  badge: string;
  detail: string;
};

// "첫 100일을 모두 이용하신 후" — 100일을 다 채웠는지만 가린다. 이용 중(정지 포함)이면 아직 세는 중이다.
// 신청 기한(100일 뒤 10일)은 담당자가 참고하도록 적기만 하고 충족 여부에는 넣지 않는다.
// 정지가 끼면 100일째 날과 신청 기한이 뒤로 밀리므로 뺀 일수를 붙인다.
export const paybackCompletionCondition = (
  review: AdminUserPaybackReview,
  isOngoing: boolean
): PaybackCondition => {
  const paused =
    review.pausedDays > 0 ? `정지 ${review.pausedDays}일 제외` : null;
  if (review.isComplete) {
    const done = [monthDayLabel(review.windowEnd), paused]
      .filter(Boolean)
      .join(", ");
    const until = review.applyUntil
      ? ` · 신청 기한 ${monthDayLabel(review.applyUntil)}`
      : "";
    return { met: true, badge: "충족", detail: `100일 완료 (${done})${until}` };
  }
  if (review.countedDays === 0)
    return { met: null, badge: "시작 전", detail: "아직 센 날이 없어요" };
  const pausedSuffix = paused ? ` (${paused})` : "";
  return isOngoing
    ? {
        met: null,
        badge: "진행 중",
        detail: `${review.countedDays}일째${pausedSuffix}`,
      }
    : {
        met: false,
        badge: "미충족",
        detail: `${review.countedDays}일에서 이용 끝남${pausedSuffix}`,
      };
};

// "페이백 제도는 첫 100일 이용에만 적용되며, 프로그램 추가 연장 이용부터는 적용되지 않습니다"
// 검토 기간이 늘 첫 결제의 100일이라 충족이다. 연장 결제는 참고로 붙인다 (연장 기간은 페이백 대상이 아니다).
export const paybackFirstPaymentCondition = (
  review: AdminUserPaybackReview
): PaybackCondition => {
  const paid = parseDateInputValue(review.paidAt);
  const paidLabel = paid
    ? `${paid.getFullYear()}년 ${monthDayLabel(review.paidAt)}`
    : review.paidAt;
  const extensions = review.extensionPaidAt.length
    ? `연장 결제 ${review.extensionPaidAt
        .map((date) => monthDayLabel(date))
        .join(", ")} (대상 아님)`
    : "연장 결제 없음";
  return {
    met: true,
    badge: "충족",
    detail: `${paidLabel} 첫 결제 · ${extensions}`,
  };
};

// "결제 이후 첫 100일 동안 최소 주 1회" — 참여 없는 날이 7일 이상 이어진 구간이 없어야 한다
export const paybackWeeklyCondition = (
  review: AdminUserPaybackReview
): PaybackCondition => {
  if (review.weeks.length === 0)
    return { met: null, badge: "센 날 없음", detail: "아직 센 날이 없어요" };
  return review.gaps.length === 0
    ? { met: true, badge: "충족", detail: "7일 이상 빈 기간 없음" }
    : {
        met: false,
        badge: "미충족",
        detail: `7일 이상 빈 기간 ${review.gaps.length}번`,
      };
};

// 페이백 금액 합계 — 첫 모달 하단과 확인 창에 보인다
export const paybackRefundTotal = (refund: AdminUserPaybackRefund) =>
  refund.items.reduce((sum, item) => sum + item.amount, 0);

// 첫 모달 하단의 금액 내역 — 결제가 둘 이상일 때만. "첫 결제 300,000원 + 플랜 업그레이드 165,000원"
export const paybackRefundBreakdown = (refund: AdminUserPaybackRefund) =>
  refund.items.length > 1
    ? refund.items
        .map((item) => `${item.label} ${formatAmount(item.amount)}`)
        .join(" + ")
    : null;

// 확인 창의 연장 결제 줄 — 페이백 대상이 아니다. "8월 20일, 11월 30일 결제 · 환불하지 않아요"
export const paybackExtensionLabel = (extensionPaidAt: string[]) =>
  `${extensionPaidAt
    .map((date) => monthDayLabel(date))
    .join(", ")} 결제 · 환불하지 않아요`;

// 확인 창의 이용 상태 줄 — 웹훅(PaymentBO)과 같은 기준으로, 구독이 "이용 종료"가 아니거나 정지 중이면 오늘 끝난다.
// 지금 상태는 상세 패널의 이용 상태 배지와 같은 이름으로 쓴다 (정지 중은 재개 예정일 대신 "정지 중").
export const paybackUsageChange = (detail: AdminUserDetail) => {
  const { subscription, pause } = detail;
  const current = pause.isPaused
    ? "정지 중"
    : subscriptionStatusLabel(subscription.status);
  const endsToday =
    pause.isPaused || subscription.status !== "subscribing_expired";
  return {
    endsToday,
    label: endsToday
      ? `${current} → 오늘 종료`
      : `${current} (이용 상태 변경 없음)`,
  };
};

// 확인 창에서 한 번 더 상기할 조건 — 충족이 아닌 것 (미충족, 아직 가릴 수 없는 진행 중·시작 전).
// 첫 모달의 조건 줄 순서대로. "주 1회 참여 — 미충족 · 7일 이상 빈 기간 2번"
export const paybackUnmetConditions = (
  review: AdminUserPaybackReview,
  isOngoing: boolean
): string[] =>
  [
    { label: "100일 이용", ...paybackCompletionCondition(review, isOngoing) },
    { label: "최초 결제", ...paybackFirstPaymentCondition(review) },
    { label: "주 1회 참여", ...paybackWeeklyCondition(review) },
  ]
    .filter((condition) => condition.met !== true)
    .map(
      (condition) =>
        `${condition.label} — ${condition.badge} · ${condition.detail}`
    );

// 확인 창의 한 줄 — "9월 28일 결제 · 300,000원"
export const paybackRefundItemLabel = (item: AdminUserPaybackRefundItem) =>
  `${monthDayLabel(item.paidAt)} 결제 · ${formatAmount(item.amount)}`;

// 페이백 응답을 결제 이력에 먼저 반영할 모양으로 — 취소한 건을 환불 응답처럼 넘긴다 (refundedPayments).
// 토스 취소는 이미 끝났고 표만 웹훅을 기다린다. 업그레이드 결제는 결제 이력 표에 없어 그대로 지나간다.
export const paybackRefundResult = (
  result: AdminUserPaybackResult
): AdminUserRefundResult => ({
  cancelAmount: result.canceled.reduce((sum, item) => sum + item.amount, 0),
  canceled: result.canceled.map((item) => ({
    orderId: item.orderId,
    cancelAmount: item.amount,
    cancelReason: "페이백",
  })),
});

// 주 1회 참여 줄 아래의 일시정지 — 빼고 센 정지·공백 구간. "일시정지 7월 1일 ~ 7월 7일 (7일), 8월 3일 (1일)"
export const paybackPausesLabel = (pauses: AdminUserPaybackPause[]) =>
  pauses.length === 0
    ? "일시정지 없음"
    : `일시정지 ${pauses
        .map(
          (pause) =>
            `${
              pause.from === pause.to
                ? monthDayLabel(pause.from)
                : `${monthDayLabel(pause.from)} ~ ${monthDayLabel(pause.to)}`
            } (${pause.days}일)`
        )
        .join(", ")}`;

// "6월 19일 ~ 6월 29일 · 11일 (정지 7일 제외)"
export const paybackGapLabel = (gap: AdminUserPaybackGap) =>
  `${monthDayLabel(gap.from)} ~ ${monthDayLabel(gap.to)} · ${gap.days}일${
    gap.pausedDays > 0 ? ` (정지 ${gap.pausedDays}일 제외)` : ""
  }`;

// 주차 표의 "참여한 날" — "5일 (정지 7일)"
export const paybackWeekDaysLabel = (week: AdminUserPaybackWeek) =>
  `${week.activeDays}일${week.pausedDays > 0 ? ` (정지 ${week.pausedDays}일)` : ""}`;

// 주차 표의 "활동" — 그 주에 각 활동을 한 날 수. "활동지 4 · 생각기록지 1 · 명상 1". 하나도 없으면 "참여 없음".
export const paybackWeekActivityLabel = (week: AdminUserPaybackWeek) => {
  const parts = (
    [
      ["활동지", week.taskDays],
      ["생각기록지", week.thoughtRecordDays],
      ["명상", week.meditationDays],
      ["검사", week.selfCheckDays],
      ["돌아보기", week.surveyDays],
    ] as const
  )
    .filter(([, days]) => days > 0)
    .map(([label, days]) => `${label} ${days}`);
  return parts.length ? parts.join(" · ") : "참여 없음";
};

export type PaybackThoughtResult = {
  label: string;
  tone: "reduced" | "same" | "increased" | "none";
};

// 자동적 사고의 영향 점수 변화 — 생각 지도의 변화 화살표와 같은 판정이다 (마지막 점수가 첫 점수보다 낮으면 줄임).
export const paybackThoughtResult = (
  thought: AdminUserPaybackThought
): PaybackThoughtResult => {
  if (thought.firstScore == null) return { label: "점수 없음", tone: "none" };
  if (thought.lastScore == null)
    return { label: "다시 매기지 않음", tone: "none" };
  const delta = thought.lastScore - thought.firstScore;
  if (delta < 0) return { label: `줄임 −${-delta}`, tone: "reduced" };
  if (delta > 0) return { label: `늘어남 +${delta}`, tone: "increased" };
  return { label: "그대로", tone: "same" };
};

// "80 → 30". 다시 매기지 않았으면 첫 점수만, 점수가 없으면 "-".
export const paybackThoughtScoreLabel = (thought: AdminUserPaybackThought) => {
  if (thought.firstScore == null) return "-";
  return thought.lastScore == null
    ? `${thought.firstScore}`
    : `${thought.firstScore} → ${thought.lastScore}`;
};

// "6개 찾음 · 4개 영향 줄임"
export const paybackThoughtSummary = (thoughts: AdminUserPaybackThought[]) =>
  `${thoughts.length}개 찾음 · ${
    thoughts.filter(
      (thought) => paybackThoughtResult(thought).tone === "reduced"
    ).length
  }개 영향 줄임`;

// 재생 시간(초)을 분으로 — 기록 한 건. 모르면 "-"
export const meditationMinutesLabel = (seconds?: number | null) =>
  seconds == null ? "-" : `${Math.round(seconds / 60)}분`;

// "118분 · 9회" — 초를 모두 더한 뒤 한 번만 반올림한다 (기록마다 반올림해 더하면 건수만큼 어긋난다).
// 재생 시간을 모르는 기록은 횟수에만 센다.
export const paybackMeditationSummary = (
  meditations: AdminUserPaybackMeditation[]
) =>
  `${Math.round(
    meditations.reduce(
      (sum, meditation) => sum + (meditation.seconds ?? 0),
      0
    ) / 60
  )}분 · ${meditations.length}회`;
